import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, ColumnContent, Stack, Text } from '@enechain/ecloud-designsystem'
import { useErrorToast, useSuccessToast } from '@enechain/eposition-ui'

import { FormPrimaryButton } from '~/components/common/FormPrimaryButton'
import { EVENT_NAME } from '~/constants/logEvent'
import { AvailableDealSimulationRelationForm } from '~/pages/Simulation/components/SimulationAndResultArea/AvailableDealSimulationSection/AvailableDealSimulationRelationForm'
import { ResultAvailableDealSimulation } from '~/pages/Simulation/components/SimulationAndResultArea/AvailableDealSimulationSection/ResultSection'
import { SimulationEmptyResult } from '~/pages/Simulation/components/SimulationAndResultArea/SimulationEmptyResult'
import { SimulationProgressing } from '~/pages/Simulation/components/SimulationAndResultArea/SimulationProgressing'
import { useAvailableDealSimulation } from '~/pages/Simulation/hooks/useAvailableDealSimulation'
import { OrderSimulationSchema } from '~/pages/Simulation/schemas'

export const AvailableDealSimulationSection: React.FC = () => {
  const { t } = useTranslation(['page'])
  const { handleSubmit, control } = useFormContext<OrderSimulationSchema>()
  const { showToast: showErrorToast } = useErrorToast({
    toastId: 'AvailableDealSimulationSection/error',
  })
  const { showToast: showSuccessToast } = useSuccessToast({
    toastId: 'AvailableDealSimulationSection/success',
  })
  const {
    invokeAvailableDealSimulation,
    progress,
    resultDetail,
    feeResults,
    isProcessing,
  } = useAvailableDealSimulation()

  const availableDealSimulationRelation = useWatch({
    control,
    name: 'availableDealSimulationRelation',
  })

  const onSubmit = handleSubmit(async (data) => {
    await invokeAvailableDealSimulation({
      value: data,
      onSuccess: () => {
        showSuccessToast({
          toastMessage: t('page:simulation.simulation_success'),
        })
      },
      onError: (error: string) => {
        showErrorToast({
          toastMessage: error,
        })
      },
    })
  })

  return (
    <Stack gap="4" paddingBottom="4">
      <Stack gap="2">
        <Text textStyle="headerMd">
          {t('page:simulation.select_avalable_deal_simulation')}
        </Text>
        <ColumnContent
          label={t('page:simulation.select_simulation')}
          headerSize="Xs"
        >
          <AvailableDealSimulationRelationForm />
        </ColumnContent>
      </Stack>
      <Box width="full" display="flex" justifyContent="end">
        <FormPrimaryButton
          onClick={onSubmit}
          disabled={availableDealSimulationRelation.length === 0}
          data-gtm-button={EVENT_NAME.BUTTON_AVAILABLE_DEAL_SIMULATION}
        >
          {t('page:simulation.run_simulation')}
        </FormPrimaryButton>
      </Box>
      {isProcessing && resultDetail === undefined && (
        <SimulationProgressing progress={progress} />
      )}
      {!isProcessing && resultDetail === undefined && <SimulationEmptyResult />}
      {!isProcessing && resultDetail !== undefined && (
        <ResultAvailableDealSimulation
          resultDetail={resultDetail}
          feeResults={feeResults}
        />
      )}
    </Stack>
  )
}
